import { parse, stringify } from 'zipson'
import CryptoJS from 'crypto-js'

import { defineStore } from 'pinia'
import type { LoginResult } from "~/apis/auth/type";
import {cache, sessionCache} from "~/common/useCache";
import {loginTokenInfoApi, refreshTokenApi} from "~/apis/auth";
import {COOKIE_EXPIRE} from "~/constants/constants";

const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY

export interface AuthState {
  ACCESS_TOKEN: string
  REFRESH_TOKEN: string
  userInfo: {
    DRIVER_TYPE: string
    DRIVER_IDX: string | number
    DRIVER_NM: string
    DRIVER_CEL: string
    CAMP_CD: string
  }
  isAuthenticated: boolean
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    ACCESS_TOKEN: '',
    REFRESH_TOKEN: '',
    userInfo: {
      DRIVER_TYPE: '',
      DRIVER_IDX: '',
      DRIVER_NM: '',
      DRIVER_CEL: '',
      CAMP_CD: '',
    },
    isAuthenticated: false
  }),

  actions: {
      init() {
        this.ACCESS_TOKEN = ''
        this.REFRESH_TOKEN = ''
        this.userInfo = {
          DRIVER_TYPE: '',
          DRIVER_IDX: '',
          DRIVER_NM: '',
          DRIVER_CEL: '',
          CAMP_CD: '',
        }
      },

    // 토큰 설정
    setTokens(ACCESS_TOKEN: string, REFRESH_TOKEN: string) {
      this.ACCESS_TOKEN = ACCESS_TOKEN
      this.REFRESH_TOKEN = REFRESH_TOKEN
    },
    // 로그인 정보 저장
    setUserInfo(user: LoginResult) {
      this.userInfo.DRIVER_IDX = user.DRIVER_IDX
      this.userInfo.DRIVER_NM = user.DRIVER_NM
      this.userInfo.DRIVER_TYPE = user.DRIVER_TYPE
      this.userInfo.DRIVER_CEL = user.DRIVER_CEL
      this.userInfo.CAMP_CD = user.CAMP_CD
    },

    // 토큰 로그인
    async tokenLogin() {
        try {
          const res = await loginTokenInfoApi()

          if (res.code === '100') {
            const date = new Date()
            // 만료 시간 구하기(만료일을 ms단위로 변경)
            date.setTime(date.getTime() + COOKIE_EXPIRE)
            const cookie = useCookie('user', { expires: date })

            //@ts-ignore
            this.setUserInfo(res.data)
            //@ts-ignore
            cookie.value = this.userInfo

            sessionCache.set('userInfo', this.userInfo)

            return true

          } else {
            this.logout()
            return false
          }

        } catch (error) {
          console.error('토큰 로그인 실패:', error)
          this.logout()
          return false
        }
    },
    // refresh token 기준 갱신 처리
    async refreshAccessToken() {
      try {
        const res = await refreshTokenApi()

        if (res.code === '100') {
          cache.set('bus-r-token', res.data?.REFRESH_TOKEN)
          sessionCache.set('bus-a-token', res.data?.ACCESS_TOKEN)
          //@ts-ignore
          this.userInfo.ACCESS_TOKEN = res.data?.ACCESS_TOKEN
          //@ts-ignore
          this.userInfo.REFRESH_TOKEN = res.data?.REFRESH_TOKEN

          // await this.tokenLogin()

          return true

        } else {
          this.logout()
          return false

        }
      } catch (error) {
        console.error('토큰 갱신 실패:', error)
        this.logout()
        return false
      }
    },

    // 로그아웃
    logout() {
      sessionCache.clear() // sessionStorage.removeItem('selectedDate')
      cache.clear()
      cache.set('lastLoginPhone', this.userInfo.DRIVER_CEL)

      const cookie = useCookie('user')
      cookie.value = null

      this.init()
      this.isAuthenticated = false
      navigateTo('/login')
    },

  },
  persist: {
    key: 'auth',
    storage: persistedState.localStorage,
    serializer: {
      deserialize: (value: string) => {
        try {
          // 암호화된 데이터 복호화
          const bytes = CryptoJS.AES.decrypt(value, ENCRYPTION_KEY)
          const decryptedData = bytes.toString(CryptoJS.enc.Utf8)
          return parse(decryptedData)
        } catch (error) {
          console.error('스토어 복호화 중 오류 발생:', error)
          return null
        }
      },
      serialize: (value: unknown) => {
        // 데이터 암호화
        const serializedData = stringify(value)
        return CryptoJS.AES.encrypt(serializedData, ENCRYPTION_KEY).toString()
      },
    },
  },
  // pwa 환경에서 세션 유지 원하면 pinia-plugin-persistedstate 사용 가능
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
